<template>
  <div class="customer-form">
    <form
      class="space-y-4"
      @submit.prevent="handleSubmit"
    >
      <!-- Required Fields -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre del Cliente *
          </label>
          <input
            v-model="form.name"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Juan Pérez"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Teléfono *
          </label>
          <input
            v-model="form.phone"
            type="tel"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="5551234567"
            @input="formatPhone"
          >
        </div>
      </div>

      <!-- Address Fields - Now managed through AddressManager -->
      <div class="space-y-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p class="font-medium mb-1">
            📍 Gestión de Direcciones
          </p>
          <p class="text-xs">
            Las direcciones de entrega se gestionan a través del sistema de múltiples direcciones después de crear el cliente. Podrás agregar, editar y seleccionar direcciones específicas para cada pedido.
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notas Adicionales del Cliente
          </label>
          <textarea
            v-model="form.notes"
            rows="2"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Preferencias del cliente, alergias, notas especiales, etc."
          />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          @click="$emit('cancel')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          :disabled="isSubmitting"
          class="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <LoadingSpinner 
            v-if="isSubmitting" 
            size="sm" 
            color="slate" 
            :show-text="false"
            container-class="-ml-1 mr-3"
          />
          {{ isSubmitting ? 'Guardando...' : 'Guardar Cliente' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import customerService from '@/services/customerService';
import type { CustomerCreate, CustomerUpdate } from '@/types/customer';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

interface Props {
  customerId?: number; // If provided, we're editing an existing customer
}

const props = defineProps<Props>();

const emit = defineEmits<{
  saved: [customer: any];
  cancel: [];
  'phone-exists': [phone: string];
}>();

const isSubmitting = ref(false);

const form = reactive<CustomerCreate & CustomerUpdate>({
  name: '',
  phone: '',
  notes: ''
});

// Format phone number (basic formatting)
const formatPhone = () => {
  // Remove all non-numeric characters
  form.phone = form.phone.replace(/\D/g, '');
};


// Load existing customer data if editing
const loadCustomer = async () => {
  if (!props.customerId) return;
  
  try {
    const customer = await customerService.getCustomer(props.customerId);
    Object.assign(form, {
      name: customer.name,
      phone: customer.phone,
      notes: customer.notes || ''
    });
  } catch (error) {
    console.error('Error loading customer:', error);
  }
};

// Handle form submission
const handleSubmit = async () => {
  if (isSubmitting.value) return;
  
  isSubmitting.value = true;
  
  try {
    let customer;
    if (props.customerId) {
      // Update existing customer
      customer = await customerService.updateCustomer(props.customerId, form);
    } else {
      // Create new customer
      customer = await customerService.createCustomer(form);
    }
    
    emit('saved', customer);
  } catch (error: any) {
    console.error('Error saving customer:', error);
    
    let errorMessage = 'Error al guardar el cliente';
    
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      
      if (detail.includes('teléfono') || detail.includes('phone')) {
        const phoneToSearch = form.phone;
        errorMessage = `Este número de teléfono ya está registrado.\n\nTeléfono: ${phoneToSearch}\n\nOpciones:\n1. Cierra este formulario y busca el cliente existente\n2. O corrige el número de teléfono`;
        
        // Emit event to parent to search for this phone
        emit('phone-exists', phoneToSearch);
      } else {
        errorMessage = detail;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    alert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  loadCustomer();
});
</script>

<style scoped>
.customer-form {
  max-width: 600px;
}
</style>
